import { useEffect, useRef } from "react"
import { useAuthStore } from "../../../stores/auth-store"
import { useCallStore } from "../../../stores/call-store"
import { api, getActivityNetworkLogs } from "../../../lib/api/client"
import { detectActions } from "../lib/action-detector"
import { getSystemInfo } from "../lib/system-info"
import type { ActivitySlice } from "../types"

const FLUSH_INTERVAL_MS = 5000

/**
 * Global activity tracker component.
 * Monitors user interactions, network requests, and system state.
 * Sends activity data to the server every 5 seconds.
 *
 * Based on original app/src/tracking/GlobalActivity.js
 */
export function ActivityTracker() {
  const { user } = useAuthStore()
  const { browserDialing, currentCall, isInQueue } = useCallStore()

  // Use refs to avoid re-renders (critical for performance)
  const clickCountRef = useRef(0)
  const keystrokeCountRef = useRef(0)
  const mouseMovedRef = useRef(false)
  const didScrollRef = useRef(false)
  const fpsRef = useRef(60)
  const frameCountRef = useRef(0)
  const lastFpsTimeRef = useRef(performance.now())
  const animationFrameRef = useRef<number | null>(null)
  const batteryRef = useRef<BatteryManager | null>(null)
  const isVisibleRef = useRef(true)

  // Call center state refs (read from store, stored in refs for flush)
  const audioSetupRef = useRef(false)
  const onCallRef = useRef(false)
  const onCallNameRef = useRef("")
  const onCallNumberRef = useRef("")
  const isInQueueRef = useRef(false)

  // Sync call center state into refs (avoids re-renders in flush interval)
  useEffect(() => {
    audioSetupRef.current = browserDialing === "ready"
    onCallRef.current = currentCall !== null
    onCallNameRef.current = currentCall?.name ?? ""
    onCallNumberRef.current = currentCall?.number ?? ""
    isInQueueRef.current = isInQueue
  }, [browserDialing, currentCall, isInQueue])

  // Network logging now handled by axios interceptor in api/client.ts

  // Set up event listeners with stable references
  useEffect(() => {
    const handleClick = () => {
      clickCountRef.current++
    }
    const handleKeydown = () => {
      keystrokeCountRef.current++
    }
    const handleMousemove = () => {
      mouseMovedRef.current = true
    }
    const handleScroll = () => {
      didScrollRef.current = true
    }
    const handleVisibilityChange = () => {
      isVisibleRef.current = document.visibilityState === "visible"
    }

    window.addEventListener("click", handleClick)
    window.addEventListener("keydown", handleKeydown)
    window.addEventListener("mousemove", handleMousemove)
    window.addEventListener("scroll", handleScroll)
    document.addEventListener("visibilitychange", handleVisibilityChange)

    return () => {
      window.removeEventListener("click", handleClick)
      window.removeEventListener("keydown", handleKeydown)
      window.removeEventListener("mousemove", handleMousemove)
      window.removeEventListener("scroll", handleScroll)
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [])

  // FPS monitoring with proper cleanup
  useEffect(() => {
    const measureFps = (now: number) => {
      frameCountRef.current++
      const elapsed = now - lastFpsTimeRef.current

      if (elapsed >= 1000) {
        fpsRef.current = Math.round((frameCountRef.current * 1000) / elapsed)
        frameCountRef.current = 0
        lastFpsTimeRef.current = now
      }

      animationFrameRef.current = requestAnimationFrame(measureFps)
    }

    animationFrameRef.current = requestAnimationFrame(measureFps)

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [])

  // Battery API
  useEffect(() => {
    if (navigator.getBattery) {
      navigator
        .getBattery()
        .then((battery) => {
          batteryRef.current = battery
        })
        .catch(() => {
          // Battery API not available
        })
    }
  }, [])

  // Align to 5-second boundary and flush
  useEffect(() => {
    if (!user) return

    // Calculate delay to align to 5-second boundary (critical for server aggregation)
    const now = new Date()
    const ms = now.getMilliseconds()
    const sec = now.getSeconds()
    const delayUntilNextTick = (5 - (sec % 5)) * 1000 - ms || 5000

    let intervalId: ReturnType<typeof setInterval>

    const flush = async () => {
      // Skip if tab not visible
      if (!isVisibleRef.current) return

      const networkLogs = getActivityNetworkLogs()
      const actions = detectActions(networkLogs)

      // Calculate total inputs for density score
      const totalInputs =
        keystrokeCountRef.current +
        clickCountRef.current +
        (mouseMovedRef.current ? 1 : 0) +
        (didScrollRef.current ? 1 : 0)

      const systemInfo = getSystemInfo(batteryRef.current)

      const slice: ActivitySlice = {
        type: "input_summary",
        timestamp: new Date().toISOString().split(".")[0] + "Z", // Normalize to second precision
        pageTitle: document.title,
        ...systemInfo,

        // Call center state
        audioSetup: audioSetupRef.current,
        onCall: onCallRef.current,
        onCallName: onCallNameRef.current,
        onCallNumber: onCallNumberRef.current,
        agentIsInQueue: isInQueueRef.current,

        keystrokes: keystrokeCountRef.current,
        clicks: clickCountRef.current,
        mouseMoved: mouseMovedRef.current,
        didScroll: didScrollRef.current,
        fps: fpsRef.current,
        interactionDensityScore: totalInputs / (FLUSH_INTERVAL_MS / 1000),

        actions,
      }

      // Reset counters
      keystrokeCountRef.current = 0
      clickCountRef.current = 0
      mouseMovedRef.current = false
      didScrollRef.current = false

      // Send to server (fire and forget)
      try {
        await api.user_activity_slices.save(slice)
      } catch (error) {
        // Silently ignore errors - activity tracking should never break the app
        console.warn("Failed to save activity slice:", error)
      }
    }

    // Start interval after aligning to 5-second boundary
    const timeoutId = setTimeout(() => {
      flush()
      intervalId = setInterval(flush, FLUSH_INTERVAL_MS)
    }, delayUntilNextTick)

    return () => {
      clearTimeout(timeoutId)
      if (intervalId) clearInterval(intervalId)
    }
  }, [user])

  return null // No UI
}
